import React, { useState } from 'react';
import { FaArrowsAltH } from '@react-icons/all-files/fa/FaArrowsAltH';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import WindowHeader from '../../../commons/WindowHeader';
import { buildGrupoPanelTitle, type EmpresaGrupoResumen } from '../grupo-panel-title';
import GrupoProveedorTabla, {
    empresaIdFromProveedorRow,
    proveedorIdFromRow,
} from './GrupoProveedorTable';
import GrupoProveedorAsignarModal from './GrupoProveedorAsignarModal';

const GrupoProveedor = (props: {
    proveedores: any[];
    grupoId?: string | null;
    empresaId?: string | null;
    empresaSeleccionada?: EmpresaGrupoResumen | null;
    canEditGrupoEmpresas?: boolean;
    filaExpandida?: boolean;
    onToggleFilaExpandida?: () => void;
    onProveedorAsignado?: () => void;
    onEliminarProveedor?: (proveedor: any) => void;
}) => {
    const {
        proveedores,
        grupoId,
        empresaId,
        empresaSeleccionada,
        canEditGrupoEmpresas = false,
        filaExpandida = false,
        onToggleFilaExpandida,
        onProveedorAsignado,
        onEliminarProveedor,
    } = props;

    const [modalOpen, setModalOpen] = useState(false);
    const [editProveedorId, setEditProveedorId] = useState<number | null>(null);
    const [editEmpresaId, setEditEmpresaId] = useState<number | null>(null);
    const [editEmpresaNombre, setEditEmpresaNombre] = useState<string | undefined>(undefined);

    const openAsignar = () => {
        setEditProveedorId(null);
        setEditEmpresaId(null);
        setEditEmpresaNombre(undefined);
        setModalOpen(true);
    };

    const openEditarProveedor = (row: any) => {
        const proveedorId = proveedorIdFromRow(row);
        const empresaIdRow = empresaIdFromProveedorRow(row);
        if (proveedorId == null || empresaIdRow == null) return;
        setEditProveedorId(proveedorId);
        setEditEmpresaId(empresaIdRow);
        setEditEmpresaNombre(
            row.empresa?.razonSocial?.trim() || row.empresa?.nombre?.trim() || undefined,
        );
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditProveedorId(null);
        setEditEmpresaId(null);
        setEditEmpresaNombre(undefined);
    };

    const panelTitle = buildGrupoPanelTitle(
        'Proveedores',
        filaExpandida ? empresaSeleccionada : null,
    );

    const empresaIdNumerico =
        empresaId != null && empresaId !== 'new' ? Number(empresaId) : NaN;
    const empresaSeleccionadaActiva =
        empresaSeleccionada != null && !Number.isNaN(empresaIdNumerico);

    const expandFilaButton =
        onToggleFilaExpandida != null ? (
            <button
                type="button"
                className="btn btn-sm btn-light d-inline-flex align-items-center justify-content-center"
                style={{ width: 36, height: 32, padding: 0 }}
                title={
                    filaExpandida
                        ? 'Restaurar ancho: mostrar de nuevo empresas junto a proveedores'
                        : 'Expandir proveedores al 100 % del ancho (ocultar empresas en esta fila)'
                }
                aria-label={
                    filaExpandida
                        ? 'Contraer panel de proveedores'
                        : 'Expandir panel de proveedores a ancho completo'
                }
                onClick={() => onToggleFilaExpandida()}
            >
                <FaArrowsAltH />
            </button>
        ) : null;

    const plusButton =
        empresaSeleccionadaActiva ? (
            <button
                type="button"
                className="btn btn-sm btn-light d-inline-flex align-items-center justify-content-center"
                style={{ width: 36, height: 32, padding: 0 }}
                disabled={!canEditGrupoEmpresas}
                title={
                    canEditGrupoEmpresas
                        ? 'Asignar compañía'
                        : 'No tiene permiso para asignar compañías'
                }
                aria-label="Asignar compañía"
                onClick={openAsignar}
            >
                <FaPlus />
            </button>
        ) : null;

    const headerActions =
        expandFilaButton != null || plusButton != null ? (
            <div className="d-flex align-items-center gap-1 flex-shrink-0">
                {expandFilaButton}
                {plusButton}
            </div>
        ) : undefined;

    const modalEmpresaId =
        editEmpresaId != null
            ? editEmpresaId
            : !Number.isNaN(empresaIdNumerico)
              ? empresaIdNumerico
              : null;
    const empresaNombre =
        editEmpresaNombre ?? empresaSeleccionada?.razonSocial ?? undefined;

    return (
        <>
            <WindowHeader
                title={panelTitle}
                headerExtraRight={headerActions}
            />
            <GrupoProveedorAsignarModal
                open={modalOpen}
                mode={editProveedorId != null ? 'edit' : 'asignar'}
                initialProveedorId={editProveedorId}
                grupoId={grupoId ?? null}
                empresaId={modalEmpresaId}
                empresaNombre={empresaNombre}
                onClose={closeModal}
                onAssigned={onProveedorAsignado}
            />
            <GrupoProveedorTabla
                proveedores={proveedores}
                onEditarProveedor={canEditGrupoEmpresas ? openEditarProveedor : undefined}
                onEliminarProveedor={onEliminarProveedor}
            />
        </>
    );
};

export default GrupoProveedor;
